import { Reducer } from "redux"
import { AuthUserType, ClientType } from "../../../Actions/Types"
import { AuthAction } from "../../Auth"
import { RouterAction, LOCATION_CHANGE } from "connected-react-router"
import { ErrorObject } from "ajv"
import { getObjectEntriesSafely } from "../../../Helpers/Functions/object"

export interface InputRenderEntry {
    values?: unknown
    errors: ErrorObject[]
    surviveReroute?: boolean
    formErrors?: unknown[]
}

export function canSubmit(
    form: InputRenderEntry | undefined
): form is InputRenderEntry {
    return (
        form !== undefined &&
        form.errors.length === 0 &&
        form.values !== undefined &&
        (form.formErrors === undefined || form.formErrors.length === 0)
    )
}

export interface InputRenderState {
    entries: {
        [id: string]: InputRenderEntry | undefined
    }
    /**
     * Existence of dirty input forms, used for showing 'Discard changes?' dialog on before leaving the route
     */
    hasDirtyForm: boolean
    visualOnboarding: boolean
}

interface InputRenderChangeAction {
    type: ClientType.inputRender_change
    payload: InputRenderEntry
    meta: { id: string }
}

interface InputRenderClearAction {
    type: ClientType.inputRender_clear
    payload: string
}

interface InputRenderDirtinessChanged {
    type: ClientType.inputRender_dirtinessChanged
    payload: boolean
}

interface InputRenderSetVisualOnboarding {
    type: ClientType.inputRender_setVisualOnboarding
    payload: boolean
}

export type InputRenderAction =
    | InputRenderChangeAction
    | InputRenderClearAction
    | InputRenderDirtinessChanged
    | InputRenderSetVisualOnboarding

const initialState: InputRenderState = {
    entries: {},
    hasDirtyForm: false,
    visualOnboarding: false
}

export const InputRenderReducer: Reducer<
    InputRenderState,
    InputRenderAction | AuthAction | RouterAction
> = (state = initialState, action): InputRenderState => {
    switch (action.type) {
        case ClientType.inputRender_change:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    [action.meta.id]: action.payload
                }
            }
        case ClientType.inputRender_clear:
            // Clearing subforms is important for UI consistency
            const cleanedEntries = Object.entries(state.entries).reduce(
                (newEntries, [entryID, input]) => {
                    return {
                        ...newEntries,
                        [entryID]: entryID.startsWith(action.payload)
                            ? undefined
                            : input
                    }
                },
                {}
            )
            return {
                ...state,
                entries: cleanedEntries
            }
        case ClientType.inputRender_dirtinessChanged:
            return { ...state, hasDirtyForm: action.payload }
        case ClientType.inputRender_setVisualOnboarding:
            return { ...state, visualOnboarding: action.payload }
        case LOCATION_CHANGE:
            const entries: [string, InputRenderEntry][] | null =
                getObjectEntriesSafely(state.entries)
            if (entries === null) return initialState
            return {
                ...state,
                entries: entries
                    .filter(e => e[1] && e[1].surviveReroute === true)
                    .reduce((prev, e) => ({ ...prev, [e[0]]: e[1] }), {}),
                hasDirtyForm: false
            }
        case AuthUserType.signedOut:
            return initialState
        default:
            return state
    }
}
